import Icons from './Icons.tsx';
import React, { useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, TextField, Checkbox, FormGroup, FormControlLabel,
  RadioGroup, Radio, Accordion, AccordionSummary, AccordionDetails,
  Stack, Button, ButtonGroup, Menu, MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { Position } from '../types/position.ts';
import { useTagsRegistry } from './TagsProvider.tsx';
import { buildTagTree } from '../utils/tags.ts';

function roundToSignificantDigits(value: number, digits = 2): number {
  if (value === 0) return 0;
  const scale = Math.pow(10, digits - Math.floor(Math.log10(Math.abs(value))) - 1);
  return Math.round(value * scale) / scale;
}
function calculateStepFromRange(min: number, max: number): number {
  const range = max - min;
  if (range === 0) return 1;
  const exponent = Math.floor(Math.log10(range)) - 1;
  return Math.pow(10, exponent);
}

function formatDisplayLabel(value: string) {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function toISOStringWithTimezone(date) {
  const timezoneOffset = date.getTimezoneOffset();

  const timezoneOffsetSign = timezoneOffset >= 0 ? "-" : "+";
  const timezoneOffsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, "0");
  const timezoneOffsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, "0");

  const dateAdjustedToTimezoneOffset = new Date(date.getTime() - timezoneOffset * 60 * 1000);

  const isoStringWithTimezone = dateAdjustedToTimezoneOffset.toISOString().replace("Z", "") + timezoneOffsetSign + timezoneOffsetHours + ":" + timezoneOffsetMinutes;
  return isoStringWithTimezone;
}

interface PathFiltersProps {
  data: Position[];
  startTime: string;
  setStartTime: (v: string) => void;
  endTime: string;
  setEndTime: (v: string) => void;
  filters: { [key: string]: any };
  setFilters: (filters: { [key: string]: any }) => void;
  user: any;
  config: Record<string, any> | null;
}

type RangePreset =
  | "Last Hour"
  | "Last 24 Hours"
  | "Last 7 Days"
  | "Last 30 Days"
  | "Last 12 Months"
  | "Today"
  | "This Week"
  | "This Month"
  | "This Year";

const presets: RangePreset[] = [
  "Last Hour",
  "Last 24 Hours",
  "Last 7 Days",
  "Last 30 Days",
  "Last 12 Months",
  "Today",
  "This Week",
  "This Month",
  "This Year",
];

const PathFilters: React.FC<PathFiltersProps> = ({
  data,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  filters,
  setFilters,
  user,
  config
}) => {
  const [rangePreset, setRangePreset] = React.useState<RangePreset>("Today");
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [initialized, setInitialized] = React.useState(false);

  // Numeric bounds
  const numericMinMax = useMemo(() => {
    const attributes = ['altitude', 'accuracy', 'speed', 'bearing', 'velocity', 'acceleration'] as const;
    const bounds: Record<string, {
      min: number;
      max: number;
      step: number;
      actualMin: number;
      actualMax: number;
      hasNull: boolean
    }> = {};
    for (const key of attributes) {
      const values = data.map(p => p[key]);
      const nonNull = values.filter((v): v is number => typeof v === 'number');
      const hasNull = values.includes(null) || values.includes(undefined);
      if (nonNull.length === 0) continue;
      const rawMin = nonNull.reduce((accum, current) => Math.min(accum, current),  Infinity);
      const rawMax = nonNull.reduce((accum, current) => Math.max(accum, current), -Infinity);
      const step = calculateStepFromRange(rawMin, rawMax);
      const roundedMin = roundToSignificantDigits(rawMin, 2);
      const roundedMax = roundToSignificantDigits(rawMax, 2);
      bounds[key] = {
        min: roundedMin,
        max: roundedMax,
        step,
        actualMin: rawMin,
        actualMax: rawMax,
        hasNull
      };
    }
    return bounds;
  }, [data]);

  // Unique categorical values
  const uniqueValues = useMemo(() => {
    const attributes = ['provider', 'source', 'travel_mode'] as const;
    const values: { [key: string]: string[] } = {};
    for (const attr of attributes) {
      values[attr] = Array.from(
        new Set(
          data.map(p => {
            const val = p[attr];
            return val === null ? 'undefined' : val;
          })
        )
      );
    }
    return values;
  }, [data]);

  // Initialize selected filters to include all current values or user preferences
  useEffect(() => {
    if (!uniqueValues) return;
    
    const storageKey = user?.id ? `filterPreferences_${user.id}` : null;
    const saved = storageKey ? localStorage.getItem(storageKey) : null;
    let userFilters: { [key: string]: any } = {};
    if (saved) {
      try {
        userFilters = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved filters', e);
      }
    }
    
    setFilters(prev => {
      const next = { ...prev };
      Object.keys(uniqueValues).forEach(k => {
        const values = uniqueValues[k];
        const userSaved = userFilters[k];
        const globalConfig = config?.[`filters.${k}`];
        let globalDefault: string[] | null = null;
        if (globalConfig && typeof globalConfig === 'string') {
          try {
            globalDefault = JSON.parse(globalConfig);
          } catch (e) {
            console.error(`Failed to parse config filters.${k}`, e);
          }
        }
        
        if (Array.isArray(prev[k]) && prev[k].length === 0) {
          // Priority: user saved > global default > all values
          if (userSaved && userSaved.length > 0) {
            // Use user saved, filter to only existing values
            next[k] = userSaved.filter((v: string) => values.includes(v));
          } else if (globalDefault && globalDefault.length > 0) {
            // Use global default, filter to only existing values
            next[k] = globalDefault.filter((v: string) => values.includes(v));
          } else {
            // Default: show all values
            next[k] = [...values];
          }
        } else {
          next[k] = (prev[k] ?? []).filter((v: string) => values.includes(v));
        }
      });
      
      // Set numeric filter modes from preferences
      const numericModes = ['accuracy', 'altitude', 'speed', 'bearing', 'velocity', 'acceleration'];
      numericModes.forEach(modeKey => {
        const userSavedMode = userFilters[`${modeKey}Mode`];
        const configMode = config?.[`filters.${modeKey}_mode`];
        if (userSavedMode) {
          next[`${modeKey}Mode`] = userSavedMode;
        } else if (configMode) {
          next[`${modeKey}Mode`] = configMode;
        } else {
          next[`${modeKey}Mode`] = prev[`${modeKey}Mode`] || 'all';
        }
      });
      
      return next;
    });
    setInitialized(true);
  }, [uniqueValues, setFilters, user, config]);

  // Save filter preferences when user modifies them
  useEffect(() => {
    if (!initialized || !user) return;
    const storageKey = `filterPreferences_${user.id}`;
    const toSave: { [key: string]: any } = {};
    ['provider', 'source', 'travel_mode'].forEach(k => {
      if (filters[k]) {
        toSave[k] = filters[k];
      }
    });
    // Also save numeric filter modes
    ['accuracy', 'altitude', 'speed', 'bearing', 'velocity', 'acceleration'].forEach(k => {
      if (filters[`${k}Mode`]) {
        toSave[`${k}Mode`] = filters[`${k}Mode`];
      }
    });
    localStorage.setItem(storageKey, JSON.stringify(toSave));
  }, [filters, initialized, user]);

  // End < Start rule based on DATE only
  const onEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndTime = e.target.value;
    const newEndDate = (newEndTime || '').split('T')[0];

    if (newEndTime && startTime && newEndTime < startTime) {
      setStartTime(`${newEndDate}T00:00`);
    }
    setEndTime(newEndTime);
  }, [startTime, setStartTime, setEndTime]);

  const startOfDay = (d: Date) => {
    const startOfDayDate = new Date(d);
    startOfDayDate.setHours(0, 0, 0, 0);
    return startOfDayDate;
  };
  const endOfDay = (d: Date) => {
    const endOfDayDate = new Date(d);
    endOfDayDate.setHours(23, 59, 59, 999);
    return endOfDayDate;
  };
  const startOfWeekMon = (d: Date) => {
    const startOfWeekMonDate = startOfDay(d);
    startOfWeekMonDate.setDate(startOfWeekMonDate.getDate() - (startOfWeekMonDate.getDay() + 6) % 7);
    return startOfWeekMonDate;
  };
  const endOfWeekMon = (d: Date) => {
    const endOfWeekMonDate = startOfWeekMon(d);
    endOfWeekMonDate.setDate(endOfWeekMonDate.getDate() + 6);
    return endOfDay(endOfWeekMonDate);
  };
  const startOfMonth = (d: Date) => {
    const startOfMonthDate = new Date(d);
    return new Date(startOfMonthDate.getFullYear(), startOfMonthDate.getMonth(), 1, 0, 0, 0, 0);
  };
  const endOfMonth = (d: Date) => {
    const endOfMonthDate = new Date(d);
    return new Date(endOfMonthDate.getFullYear(), endOfMonthDate.getMonth() + 1, 0, 23, 59, 59, 999);
  };
  const startOfYear = (d: Date) => {
    const startOfYearDate = new Date(d);
    return new Date(startOfYearDate.getFullYear(), 0, 1, 0, 0, 0, 0);
  };
  const endOfYear = (d: Date) => {
    const endOfYearDate = new Date(d);
    return new Date(endOfYearDate.getFullYear(), 11, 31, 23, 59, 59, 999);
  };

  const applyPreset = React.useCallback((p: RangePreset) => {
    const now = new Date();
    let newStartTime: Date;
    let newEndTime: Date;

    switch (p) {
      case "Last Hour":
        newEndTime = now;
        newStartTime = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case "Last 24 Hours":
        newEndTime = now;
        newStartTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "Last 7 Days":
        newStartTime = new Date(startOfDay(now).getTime() - 6 * 24 * 60 * 60 * 1000);
        newEndTime = endOfDay(now);
        break;
      case "Last 30 Days":
        newStartTime = new Date(startOfDay(now).getTime() - 29 * 24 * 60 * 60 * 1000);
        newEndTime = endOfDay(now);
        break;
      case "Last 12 Months":
        newStartTime = new Date(startOfMonth(now).setFullYear(startOfMonth(now).getFullYear() - 1));
        newEndTime = endOfMonth(now);
        break;
      case "Today":
        newStartTime = startOfDay(now);
        newEndTime = endOfDay(now);
        break;
      case "This Week":
        newStartTime = startOfWeekMon(now);
        newEndTime = endOfWeekMon(now);
        break;
      case "This Month":
        newStartTime = startOfMonth(now);
        newEndTime = endOfMonth(now);
        break;
      case "This Year":
        newStartTime = startOfYear(now);
        newEndTime = endOfYear(now);
        break;
    }

    setStartTime(toISOStringWithTimezone(newStartTime).slice(0, 16));
    setEndTime(toISOStringWithTimezone(newEndTime).slice(0, 16));
  }, [setStartTime, setEndTime]);

  const handleMainClick = React.useCallback(() => {
    applyPreset(rangePreset); // re-apply current selection
  }, [applyPreset, rangePreset]);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handleSelect = (p: RangePreset) => {
    setRangePreset(p);
    applyPreset(p);   // apply immediately when selected
    closeMenu();
  };

  const isFullDayRange = (a: Date, b: Date) =>
    a.getHours() === 0 && a.getMinutes() === 0 &&
    b.getHours() === 23 && b.getMinutes() === 59;

  const isFullMonthRange = (a: Date, b: Date) =>
    isFullDayRange(a, b) &&
    a.getDate() === 1 &&
    b.getDate() === new Date(b.getFullYear(), b.getMonth() + 1, 0).getDate();

  const shiftByRange = React.useCallback((direction: 1 | -1) => {
    if (!startTime || !endTime) return;

    const start = new Date(startTime);
    const end = new Date(endTime);

    // Guard: if invalid or reversed, bail
    if (!(start instanceof Date) || isNaN(start.getTime())) return;
    if (!(end instanceof Date) || isNaN(end.getTime())) return;
    if (end <= start) return;

    let newStartTime: Date;
    let newEndTime: Date;

    if (isFullMonthRange(start, end)) {
      const durationMonths = (end.getMonth() - start.getMonth() + 12) % 12 + 1;
      newStartTime = new Date(startOfMonth(start.setMonth(start.getMonth() + direction * durationMonths)));
      newEndTime = new Date(endOfMonth(startOfMonth(end).setMonth(end.getMonth() + direction * durationMonths)));
    } else if (isFullDayRange(start, end)) {
      const durationDays = Math.round((startOfDay(end).getTime() - start.getTime()) / 24 / 60 / 60 / 1000) + 1;
      newStartTime = new Date(startOfDay(start.getTime() + direction * durationDays * 24 * 60 * 60 * 1000));
      newEndTime = new Date(endOfDay(end.getTime() + direction * durationDays * 24 * 60 * 60 * 1000));
    } else {
      const durationMilliseconds = end.getTime() - start.getTime();
      newStartTime = new Date(start.getTime() + direction * durationMilliseconds);
      newEndTime = new Date(end.getTime() + direction * durationMilliseconds);
    }

    setStartTime(toISOStringWithTimezone(newStartTime).slice(0, 16));
    setEndTime(toISOStringWithTimezone(newEndTime).slice(0, 16));
  }, [startTime, endTime, setStartTime, setEndTime]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Accordion key={'datetime'} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content['datetime']}
            <Typography sx={{ ml: 1 }}>Date and Time</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Start Time"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <TextField
              label="End Time"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={endTime}
              onChange={onEndChange}
            />
            <ButtonGroup variant="outlined" fullWidth>
              <Button size="small" onClick={openMenu} aria-controls={menuAnchor ? "range-menu" : undefined} aria-haspopup="menu" aria-expanded={Boolean(menuAnchor) ? "true" : undefined} sx={{ minWidth: 44, width: 44 }}>
                <ArrowDropDownIcon />
              </Button>
              <Button onClick={handleMainClick}>{rangePreset}</Button>
            </ButtonGroup>
            <Menu id="range-menu" anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} fullWidth>
              {presets.map((p) => (
                <MenuItem key={p} selected={p === rangePreset} onClick={() => handleSelect(p)}>
                  {p}
                </MenuItem>
              ))}
            </Menu>
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button variant="outlined" onClick={() => shiftByRange(-1)} sx={{ flex: 1 }}>Previous</Button>
              <Button variant="outlined" onClick={() => shiftByRange(1)} sx={{ flex: 1 }}>Next</Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
      {['altitude', 'accuracy', 'bearing', 'speed', 'velocity', 'acceleration'].map(key => {
        const bounds = numericMinMax[key];
        return bounds ? (
          <Accordion key={key}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {Icons.drawer.content[key]}
                <Typography sx={{ ml: 1 }}>{formatDisplayLabel(key)}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                type="number"
                label="Min"
                fullWidth
                inputProps={{
                  min: bounds.min,
                  max: Math.min(filters[`${key}Max`], bounds.actualMax),
                  step: bounds.step
                }}
                value={filters[`${key}Min`] ?? bounds.min}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    [`${key}Min`]: e.target.value === '' ? null : parseFloat(e.target.value)
                  }))
                }
                sx={{ mb: 2 }}
              />
              <TextField
                type="number"
                label="Max"
                fullWidth
                inputProps={{
                  min: Math.max(filters[`${key}Min`], bounds.actualMin),
                  max: bounds.max,
                  step: bounds.step
                }}
                value={filters[`${key}Max`] ?? bounds.max}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    [`${key}Max`]: e.target.value === '' ? null : parseFloat(e.target.value)
                  }))
                }
                sx={{ mb: 2 }}
              />

              {bounds.hasNull && (
                <RadioGroup
                  value={filters[`${key}Mode`]}
                  onChange={e => setFilters(prev => ({
                    ...prev, [`${key}Mode`]: e.target.value
                  }))}
                  row
                  sx={{ justifyContent: 'space-between', px: 2, flexWrap: 'wrap' }}
                >
                  <FormControlLabel value="all" control={<Radio />} label="All" />
                  <FormControlLabel value="onlyNonNull" control={<Radio />} label="Only Values" />
                  <FormControlLabel value="onlyNull" control={<Radio />} label="Only Undefined" />
                </RadioGroup>
              )}
            </AccordionDetails>
          </Accordion>
        ) : null;
      })}

      {['provider', 'source', 'travel_mode'].map(key =>
        uniqueValues[key]?.length > 1 && (
          <Accordion key={key}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {Icons.drawer.content[key]}
                <Typography sx={{ ml: 1 }}>{formatDisplayLabel(key)}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                {uniqueValues[key].map((val: string) => (
                  <FormControlLabel
                    key={val}
                    control={
                      <Checkbox
                        checked={filters[key]?.includes(val)}
                        onChange={e => {
                          const current = new Set(filters[key] ?? []);
                          e.target.checked ? current.add(val) : current.delete(val);
                          setFilters(prev => ({ ...prev, [key]: Array.from(current) }));
                        }}
                      />
                    }
                    label={formatDisplayLabel(val)}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        )
      )}

      <TagCategoryFilters filters={filters} setFilters={setFilters} />
    </Box>
  );
};

// -----------------------------------------------------------------------------
// One accordion per effective tag category. State shape:
//   filters.tag_category_<id> = { tagIds: number[]; includeUntagged: boolean }
// -----------------------------------------------------------------------------
const TagCategoryFilters: React.FC<{
  filters: { [key: string]: any };
  setFilters: (updater: any) => void;
}> = ({ filters, setFilters }) => {
  const { registry } = useTagsRegistry();
  if (registry.effectiveCategories.length === 0) return null;

  return (
    <>
      {registry.effectiveCategories.map(cat => {
        const key = `tag_category_${cat.id}`;
        const state = filters[key] ?? { tagIds: [], includeUntagged: true };
        const catTags = Array.from(registry.tags.values()).filter(t => t.category_id === cat.id);
        if (catTags.length === 0) return null;
        const tree = buildTagTree(catTags);

        const setState = (patch: Partial<{ tagIds: number[]; includeUntagged: boolean }>) =>
          setFilters((prev: any) => ({ ...prev, [key]: { ...state, ...patch } }));

        const collectDescendantIds = (nodeList: ReturnType<typeof buildTagTree>): number[] => {
          const ids: number[] = [];
          for (const n of nodeList) {
            ids.push(n.tag.id);
            ids.push(...collectDescendantIds(n.children));
          }
          return ids;
        };

        return (
          <Accordion key={key}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalOfferIcon fontSize="small" />
                <Typography sx={{ ml: 1 }}>{cat.tag_category}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.includeUntagged}
                      onChange={(e) => setState({ includeUntagged: e.target.checked })}
                    />
                  }
                  label="(untagged)"
                />
                <TagFilterTree
                  tree={tree}
                  selected={new Set(state.tagIds)}
                  depth={0}
                  onToggle={(id, subtreeIds, checked) => {
                    const next = new Set(state.tagIds);
                    const targets = [id, ...subtreeIds];
                    for (const t of targets) checked ? next.add(t) : next.delete(t);
                    setState({ tagIds: Array.from(next) });
                  }}
                  collectDescendantIds={collectDescendantIds}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
};

const TagFilterTree: React.FC<{
  tree: ReturnType<typeof buildTagTree>;
  selected: Set<number>;
  depth: number;
  onToggle: (id: number, subtreeIds: number[], checked: boolean) => void;
  collectDescendantIds: (nodeList: ReturnType<typeof buildTagTree>) => number[];
}> = ({ tree, selected, depth, onToggle, collectDescendantIds }) => (
  <>
    {tree.map(node => (
      <React.Fragment key={node.tag.id}>
        <FormControlLabel
          sx={{ pl: depth * 2 }}
          control={
            <Checkbox
              checked={selected.has(node.tag.id)}
              onChange={(e) => onToggle(
                node.tag.id,
                collectDescendantIds(node.children),
                e.target.checked,
              )}
            />
          }
          label={node.tag.tag}
        />
        {node.children.length > 0 && (
          <TagFilterTree
            tree={node.children}
            selected={selected}
            depth={depth + 1}
            onToggle={onToggle}
            collectDescendantIds={collectDescendantIds}
          />
        )}
      </React.Fragment>
    ))}
  </>
);

export default PathFilters;
